import {
  cn,
  FormControl,
  FormControlError,
  FormControlErrorText,
  Input,
  InputField,
  InputSlot,
} from '@vidahealth/vida-ui';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { View, type StyleProp, type TextStyle } from 'react-native';

import { FloatingLabel } from '../FloatingLabel';

type InputFieldProps = ComponentProps<typeof InputField>;

type CommonProps = {
  size?: ComponentProps<typeof Input>['size'];
  /**
   * Optional transform applied to every keystroke before the value is written
   * to the form state. Useful for masking (date, phone, SSN, etc.) and for
   * stripping disallowed characters.
   */
  format?: (raw: string) => string;
  /** Slot rendered inside <Input> before the InputField (e.g. an icon prefix). */
  startSlot?: ReactNode;
  /** Slot rendered inside <Input> after the InputField (e.g. an action button). */
  endSlot?: ReactNode;
  /**
   * Floating label rendered above the input border. Color shifts to the focus
   * color when the field is focused, and to the error color when invalid.
   */
  label?: ReactNode;
  /**
   * Extra Tailwind classes merged onto the inner <InputField>. Use for layout
   * tweaks like padding overrides. Cannot beat vida-ui's theme-resolved
   * padding (which lives in an inline style array); reach for
   * `inputFieldStyle` when a numeric padding override is needed.
   */
  inputFieldClassName?: string;
  /**
   * Inline style merged onto the inner <InputField>. Required for overriding
   * vida-ui's theme-driven padding (passed via its style array, so it wins
   * over Tailwind classes).
   */
  inputFieldStyle?: StyleProp<TextStyle>;
  /**
   * Renders the field greyed + non-editable. The FormControl's `disabled`
   * attribute greys it (and blocks focus on web), and `editable={false}` is
   * mapped by vida-ui to the web `readOnly` attribute. Use for values the
   * backend already owns — e.g. a pre-filled name/email on a returning user.
   */
  isDisabled?: boolean;
};

type RhfProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  value?: never;
  onChangeText?: never;
  errorMessage?: never;
};

type ControlledProps = {
  control?: never;
  name?: never;
  value: string;
  onChangeText: (text: string) => void;
  errorMessage?: string;
};

export type TextFieldProps<TFieldValues extends FieldValues> = Omit<
  InputFieldProps,
  'value' | 'onChangeText' | 'onBlur'
> &
  CommonProps &
  (RhfProps<TFieldValues> | ControlledProps) & {
    onBlur?: () => void;
  };

export function TextField<TFieldValues extends FieldValues>(props: TextFieldProps<TFieldValues>) {
  if ('control' in props && props.control) {
    const { control, name, ...rest } = props;
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <TextFieldInner
            {...rest}
            value={(field.value as string | undefined) ?? ''}
            onChangeText={(text) => {
              const next = rest.format ? rest.format(text) : text;
              field.onChange(next);
            }}
            onBlur={() => {
              field.onBlur();
              rest.onBlur?.();
            }}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
    );
  }
  const { value, onChangeText, errorMessage, format, ...rest } = props as ControlledProps &
    Omit<TextFieldProps<TFieldValues>, keyof RhfProps<TFieldValues>>;
  return (
    <TextFieldInner
      {...rest}
      value={value}
      onChangeText={(text) => onChangeText(format ? format(text) : text)}
      errorMessage={errorMessage}
    />
  );
}

type InnerProps = CommonProps &
  Omit<InputFieldProps, 'value' | 'onChangeText' | 'onBlur'> & {
    value: string;
    onChangeText: (text: string) => void;
    onBlur?: () => void;
    errorMessage?: string;
  };

function TextFieldInner({
  size = 'lg',
  startSlot,
  endSlot,
  label,
  value,
  onChangeText,
  onBlur,
  errorMessage,
  onFocus,
  inputFieldClassName,
  inputFieldStyle,
  isDisabled,
  placeholder,
  format: _format,
  ...inputProps
}: InnerProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [fieldHeight, setFieldHeight] = useState(0);
  // The field is "active" (label floated) while focused or non-empty; it drops
  // back to the placeholder position only on blur-while-empty.
  const active = isFocused || value.length > 0;

  const hasError = !!errorMessage;
  // Label color: focus → primary, error → error, filled+blurred → default text,
  // resting (acting as the placeholder) → muted placeholder color.
  const labelColorClass = hasError
    ? 'text-error-700'
    : isFocused
      ? 'text-primary-500'
      : active
        ? 'text-typography-700'
        : 'text-typography-500';

  // The label doubles as the placeholder: an explicit `label` (or the
  // placeholder) rests inside the field and animates up + shrinks on focus / when
  // filled. It replaces the native placeholder, so the input keeps an
  // accessibilityLabel for screen readers.
  const fieldLabel = label ?? placeholder;
  // With a distinct `label`, the placeholder becomes an in-field hint (e.g. the
  // date mask) shown only once the label floats up — before that, the resting
  // label occupies the placeholder slot, so showing both would overlap. Without
  // a `label`, the placeholder *is* the floating label and is never rendered
  // natively.
  const nativePlaceholder = label != null && active ? placeholder : undefined;

  return (
    <FormControl isInvalid={hasError} isDisabled={isDisabled}>
      <View className='relative' onLayout={(e) => setFieldHeight(e.nativeEvent.layout.height)}>
        <Input size={size}>
          {startSlot ? <InputSlot className='p-2 pl-4'>{startSlot}</InputSlot> : null}
          <InputField
            className={cn('placeholder:text-typography-500', inputFieldClassName)}
            style={inputFieldStyle}
            // The animated label replaces the placeholder-as-label; a distinct
            // `placeholder` (paired with a separate `label`) still renders
            // natively as an in-field hint once the label floats. Keep the field
            // announced to screen readers either way.
            accessibilityLabel={typeof fieldLabel === 'string' ? fieldLabel : undefined}
            placeholder={nativePlaceholder}
            value={value}
            // vida-ui maps `editable` to the web `readOnly` attribute; combined
            // with the FormControl's `disabled` attribute (from isDisabled), the
            // field is genuinely non-editable on web + native — no guard needed.
            editable={!isDisabled}
            onChangeText={onChangeText}
            onFocus={(e: Parameters<NonNullable<InputFieldProps['onFocus']>>[0]) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            {...inputProps}
          />
          {endSlot ? <InputSlot className='pl-1 pr-4'>{endSlot}</InputSlot> : null}
        </Input>
        {fieldLabel ? (
          <FloatingLabel
            label={fieldLabel}
            active={active}
            fieldHeight={fieldHeight}
            colorClass={labelColorClass}
          />
        ) : null}
      </View>
      {errorMessage ? (
        <FormControlError>
          <FormControlErrorText size='sm' className='ml-1 mt-1'>
            {errorMessage}
          </FormControlErrorText>
        </FormControlError>
      ) : null}
    </FormControl>
  );
}
